<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';

interface Props {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  customClasses?: string;
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  size: 'lg',
  customClasses: '',
});

const emit = defineEmits<{
  close: [];
}>();

const attrs = useAttrs();

const { elementRef: modalContent } = useFocusTrap();

// Create a unique modal ID for this instance
const modalId = `modal-${useId()}`;

// Provide the modal ID to child components so they can mark their teleported elements
provide('modalId', modalId);

// Handle click outside, but ignore clicks on modal-owned teleported elements
onClickOutside(modalContent, (event) => {
  const clickTarget = event.target as HTMLElement;
  if (clickTarget?.closest(`[data-modal-owner="${modalId}"]`)) {
    return;
  }

  emit('close');
});

const variantProps = computed(() => {
  return twMerge(
    cva('w-full overflow-y-auto overflow-y-hidden rounded-2xl bg-white', {
      variants: {
        size: {
          'xs': 'max-w-[300px]',
          'sm': 'max-w-sm',
          'md': 'max-w-md',
          'lg': 'max-w-lg',
          'xl': 'max-w-xl',
          '2xl': 'max-w-2xl',
          '3xl': 'max-w-3xl',
          '4xl': 'max-w-4xl',
        },
      },
    })({
      size: props.size,
    }),
  );
});

const contentClasses = computed(() => {
  return twMerge(cva('w-full')({}), props.customClasses);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-bind="attrs"
      aria-modal="true"
      class="bg-opacity-70 fixed top-0 left-0 z-50 flex size-full items-center justify-center overflow-hidden bg-black/70 px-4"
      role="dialog"
    >
      <div
        ref="modalContent"
        :class="variantProps"
        class="p-0"
        @keydown.esc="emit('close')"
      >
        <!-- the close button section -->
        <div class="relative w-full">
          <KUICoreClickable
            class="text-muted-foreground absolute top-4 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-full p-2 transition-transform hover:rotate-90"
            data-testid="close-modal-button"
            @click="emit('close')"
            @keydown.esc="emit('close')"
          >
            <Icon :name="ICONS.cancel" size="sm" />
          </KUICoreClickable>
        </div>
        <!-- the modal content section (header + body) -->
        <div class="max-h-[calc(100vh-4em)] overflow-x-hidden p-6 shadow-xs">
          <div class="flex w-full items-start justify-between text-left">
            <div>
              <slot name="heading" />
            </div>
          </div>
          <div :class="contentClasses" class="relative">
            <slot />
          </div>
          <!-- Footer -->
          <div v-if="$slots.footer" class="px-6 py-3">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
